import { toast } from "react-toastify";
import { startShowLoader, stopShowLoader } from "../../../store/loader";
import { addExtraOfferToAdditional, removeExtraFromAdditional, resetQuotation, setEditCalculatedQuotation, setInitializeQuotation, setOfferedExtras, setUpgradeQuotation, setUpgradeQuotationConfirmation } from "./quotationSlice";
import i18n from "../../../config/lang";
import { axiosClient, URLRoutes } from "../../../config";
import { GET_EXTRAS_URL, GET_QUOTATION_URL, GET_UPGRADE_URL } from "../../../utilities/constants";
import { createAsyncThunk } from "@reduxjs/toolkit";

const t = i18n.t;
export const startInitializeQuotation = () => {
    return async( dispatch, getState ) => {
        try {
            const { originalReservation: reservationQuotationData } = getState().reservation;
            dispatch( startShowLoader() );

            await (dispatch( setInitializeQuotation({
                pricing: reservationQuotationData.pricing,
                class: reservationQuotationData.class,
                extras: reservationQuotationData.pricing.extras,
                rentalDays: reservationQuotationData.rentalDays,
                currencyInfo: reservationQuotationData.currencyInfo,
                pickupInfo: {
                    pickupDate: reservationQuotationData.pickupDate,
                    pickupCode: reservationQuotationData.pickupCode,
                    pickupLocation: reservationQuotationData.pickupLocation,
                },
                dropoffInfo: {
                    returnDate: reservationQuotationData.returnDate,
                    returnCode: reservationQuotationData.returnCode,
                    returnLocation: reservationQuotationData.returnLocation,
                },
                reservationInfo: {
                    id: reservationQuotationData.reservationId,
                    code: reservationQuotationData.reservationCode,
                    discount: reservationQuotationData.pricing.discounts
                }
            })));
            dispatch( stopShowLoader() );
            return true;
        } catch (error) {
            toast.error(t('Error.unexpected_error_occurred', 'An unexpected error occurred, please try again later.'));
            return false;
        }
    }
}

export const startGetOfferedExtras = ({ currencyCode, pickupCode, classCode }) => {
    return async( dispatch ) => {
        dispatch( startShowLoader() );
        const { status: statusResponse, data: response } = await axiosClient.post(GET_EXTRAS_URL, {
            currencyCode,
            pickupCode,
            classCode
        })
        .catch(e => e);

        dispatch( stopShowLoader() );
        if (statusResponse != 200 || response?.type != "success") {
            toast.error(t('Error.unexpected_error_occurred', 'An unexpected error occurred, please try again later.'));
            return false;
        }

        dispatch( setOfferedExtras(response.data) );
        return true;
    }
}

export const startConfirmUpgrade = createAsyncThunk(
    'quotation/startConfirmUpgrade',
    async ({ confirm }, { dispatch, getState }) => {
        await dispatch( setUpgradeQuotationConfirmation(confirm) );
        const extrasApplied = [...getState().quotation.additionalExtras.map(x => ({ code: x.code, itemQty: x.itemQty }))];
        const { currencyInfo: { currencyCode }, pickupInfo: { pickupCode }, classes: { code: classCode } } = getState().quotation.calculatedQuotation;
        await dispatch( startCalculateQuotation({ extrasApplied }) );
        return dispatch( startGetOfferedExtras({ currencyCode, pickupCode, classCode }) );
    }
);

export const startGetUpgrade = createAsyncThunk(
    'quotation/startGetUpgrade',
    async ({ navigate }, { dispatch, getState }) => {
        dispatch( startShowLoader() );

        const {
            reservation : { originalReservation: { class: { code } } },
            quotation: { calculatedQuotation: {
                currencyInfo: { currencyId }
            } }
        } = getState();

        const { status: statusResponse, data: response } = await axiosClient.post(GET_UPGRADE_URL, {
            ClassCode: code,
            OfficeId: OFFICE.id,
            CurrencyId: currencyId
        })
        .catch(e => e);

        dispatch( stopShowLoader() );
        if (statusResponse != 200 || response?.type != "success") {
            if(!!response.message){
                await dispatch( startConfirmUpgrade({ confirm: false }) );
                navigate(URLRoutes.quotationExtras());
                return false;
            }
            toast.error(t('Error.unexpected_error_occurred', 'An unexpected error occurred, please try again later.'));
            return false;
        }

        dispatch( setUpgradeQuotation({ ...response.data }) );
        return true;
    }
);

export const startAddOfferedExtraToApplied = createAsyncThunk(
    'quotaiton/startAddOfferedExtraToApplied',
    async ({ code, isCountable, qty: itemQty }, { dispatch, getState }) => {
        const { additionalExtras } = getState().quotation;
        if(!isCountable){
            itemQty = 1
        }
        const extrasApplied = [...additionalExtras.map(x => ({ code: x.code, itemQty: x.itemQty })).filter(x => x.code != code), { code, itemQty: itemQty }];
        dispatch( startCalculateQuotation({ extrasApplied }) );
        dispatch( addExtraOfferToAdditional({ code, itemQty }) );
    }
);

export const startRemoveOfferedExtraFromApplied = createAsyncThunk(
    'quotation/startRemoveOfferedExtraFromApplied',
    async ({ code, isCountable, qty: itemQty }, { dispatch, getState }) => {
        if(!isCountable){
            itemQty = 1
        }
        const additionalExtras = getState().quotation.additionalExtras.map(x => ({ code: x.code, itemQty: x.itemQty }));
        additionalExtras.find(x => x.code == code).itemQty = itemQty;
        const extrasApplied = isCountable && itemQty != 0 ? additionalExtras :additionalExtras.filter(x => x.code != code);
        dispatch( startCalculateQuotation({ extrasApplied }) );
        dispatch( removeExtraFromAdditional({ code, itemQty }) );
    }
);

export const startCalculateQuotation = createAsyncThunk(
    'quotation/startCalculateQuotation',
    async ({ extrasApplied }, { dispatch, getState }) => {
        dispatch( startShowLoader() );
        const { upgradeQuotation: { confirmation: upgradeQuotationConfirmation, data: { pricePerDay } }, calculatedQuotation: { reservationInfo } } = getState().quotation;
        const { originalReservation: { pricing: { rate: { breakdown, extraDayPrice } }, currencyId } } = getState().reservation;
        if (upgradeQuotationConfirmation == true) {
            extrasApplied.push({
                code: "UP",
                itemQty: 1
            });
        }
        const { status: statusResponse, data: response } = await axiosClient.post(GET_QUOTATION_URL, {
            currencyId,
            reservationId: reservationInfo.id,
            extras: extrasApplied,
            extrasEdited: upgradeQuotationConfirmation == true ? [{code: "UP", price: pricePerDay}]: null,
            rateEdited: JSON.stringify([...breakdown, {type:"X-Day", typeId: 6, unitPrice: extraDayPrice, qty:0}]),
            PromoCode: reservationInfo.discount?.discountBy || null
        })
        .catch(e => e);

        dispatch( stopShowLoader() );
        if (statusResponse != 200 || response?.type != "success") {
            toast.error(t('Error.unexpected_error_occurred', 'An unexpected error occurred, please try again later.'));
            return;
        }
        dispatch( setEditCalculatedQuotation({
            ...response.data.pricing,
            rate: { rateCode: response.data.rateCode, RateId: response.data.rateId, subtotal: response.data.pricing.rate.subtotal },
            currencyInfo: {
                currencyId: response.data.currencyId,
                currencyCode: response.data.currencyCode,
                currencyExchange: response.data.exchangeRate
            }
        }) );
    }
);

export const startResetQuotation = () => {
    return async ( dispatch ) => {
        try {
            dispatch( resetQuotation() );
        } catch (error) {
            console.error(error);
            toast.error(t('Error.unexpected_error_occurred', 'An unexpected error occurred, please try again later.'));
            return false;
        }
    }
}
